import { useEffect } from "react";
import { Loader2, Sparkles } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/flowly/infra/Button/Button";
import { cn } from "@/lib/utils";
import { captureEvent } from "@/lib/analyticsQueue";
import { useFeatureRequestDialog } from "@/contexts/FeatureRequestDialogContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import {
  FEATURE_REQUEST_REWARD_CREDITS,
  type FeatureRequestCategory,
} from "@shared/schemas/featureRequest";

import { FEATURE_REQUEST_CATEGORY_OPTIONS } from "./constants";
import { useFeatureRequestForm } from "./useFeatureRequestForm";

/**
 * Global "Suggest a feature" dialog. Mounted once at the app root. Every
 * entry point calls `useFeatureRequestDialog().open(<source>)` to surface it.
 *
 * Form layout mirrors the existing AuthModal / Edit-Profile dialog style:
 * shadcn Dialog + Input/Textarea/Select + a Button submit row.
 */
export function FeatureRequestDialog() {
  const { isOpen, source, close } = useFeatureRequestDialog();
  const { t } = useLanguage();
  const form = useFeatureRequestForm();

  // Fire opened / dismissed analytics — opened on mount-of-open, dismissed
  // when the dialog closes WITHOUT a successful submission (the form reset
  // inside `handleSubmit` does its own close, but `isSubmitting` flips back
  // to false after a successful submit, so we key the dismissed-detection
  // on `isSubmitting` being false at close time + isValid). Simpler & robust:
  // just emit `opened` on every open, and `dismissed` on every close that
  // wasn't the result of a successful submit — the submitted event already
  // tells us a successful submit happened.
  useEffect(() => {
    if (isOpen) {
      captureEvent(ANALYTICS_EVENTS.FEATURE_REQUEST_DIALOG_OPENED, { source });
    }
  }, [isOpen, source]);

  const handleOpenChange = (next: boolean) => {
    if (!next && isOpen) {
      // The user closed the dialog without submitting (submit closes via
      // `close()` and form.reset() — by the time onOpenChange fires the
      // form is already cleared, so distinguishing here would be unreliable).
      // We capture every close as `dismissed`; pair with `submitted` in
      // analyses to compute drop-off.
      captureEvent(ANALYTICS_EVENTS.FEATURE_REQUEST_DIALOG_DISMISSED, { source });
      form.reset();
      close();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-background/95 backdrop-blur-xl border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-start flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {t.featureRequest.dialogTitle}
          </DialogTitle>
          <DialogDescription className="text-start">
            {t.featureRequest.dialogDescription.replace(
              "{count}",
              String(FEATURE_REQUEST_REWARD_CREDITS),
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="fr-title" className="block text-start">
              {t.featureRequest.titleLabel}
            </Label>
            <Input
              id="fr-title"
              type="text"
              maxLength={form.titleMax}
              placeholder={t.featureRequest.titlePlaceholder}
              value={form.title}
              onChange={(e) => form.setTitle(e.target.value)}
              disabled={form.isSubmitting}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="fr-description" className="block text-start">
                {t.featureRequest.descriptionLabel}
              </Label>
              <span
                className={cn(
                  "text-xs tabular-nums",
                  form.description.trim().length > 0 &&
                    form.description.trim().length < form.descriptionMin
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-muted-foreground",
                )}
              >
                {form.description.length} / {form.descriptionMax}
                {form.description.trim().length > 0 &&
                  form.description.trim().length < form.descriptionMin && (
                    <>
                      {" · "}
                      {t.featureRequest.descriptionMinHint.replace(
                        "{min}",
                        String(form.descriptionMin),
                      )}
                    </>
                  )}
              </span>
            </div>
            <Textarea
              id="fr-description"
              maxLength={form.descriptionMax}
              rows={5}
              placeholder={t.featureRequest.descriptionPlaceholder}
              value={form.description}
              onChange={(e) => form.setDescription(e.target.value)}
              disabled={form.isSubmitting}
              className="resize-none"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="fr-category" className="block text-start">
              {t.featureRequest.categoryLabel}
            </Label>
            <Select
              value={form.category}
              onValueChange={(v) => form.setCategory(v as FeatureRequestCategory)}
              disabled={form.isSubmitting}
            >
              <SelectTrigger id="fr-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FEATURE_REQUEST_CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t.featureRequest.category[opt.labelKey]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Phone (optional) */}
          <div className="space-y-2">
            <Label htmlFor="fr-phone" className="block text-start">
              {t.featureRequest.phoneLabel}
            </Label>
            <Input
              id="fr-phone"
              type="tel"
              inputMode="tel"
              maxLength={form.phoneMax}
              placeholder={t.featureRequest.phonePlaceholder}
              value={form.phone}
              onChange={(e) => form.setPhone(e.target.value)}
              disabled={form.isSubmitting}
            />
          </div>

          {/* Notify on ship */}
          <label
            htmlFor="fr-notify"
            className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer"
          >
            <Checkbox
              id="fr-notify"
              checked={form.notifyOnShip}
              onCheckedChange={(v) => form.setNotifyOnShip(v === true)}
              disabled={form.isSubmitting}
              className="mt-0.5"
            />
            <span className="leading-snug">{t.featureRequest.notifyOnShip}</span>
          </label>

          {/* Submit — stays enabled even while the form is incomplete.
              Validation happens on click and surfaces a specific toast,
              so the user always knows what's missing instead of staring
              at a disabled button. */}
          <Button
            variant="primary"
            className="w-full"
            onClick={form.handleSubmit}
            disabled={form.isSubmitting}
          >
            {form.isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.featureRequest.submitting}
              </>
            ) : (
              t.featureRequest.submit
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
